import cn from 'classnames'
import { ReactNode } from 'react'

import { CockpitTableColumnTitle } from '../../../components/cockpitTableTitle'

import styles from './CockpitTileGroupList.module.scss'

export interface ICockpitTileGroupColumn {
  label: string
  isNumeric?: boolean
  /** Share of the table width, e.g. `'18%'`. Given for every column so each group's table lays
   *  its columns out identically — without it the browser sizes them per group's own content and
   *  the stack of tables reads ragged. */
  width?: string
}

export interface ICockpitTileGroupSummaryColumn {
  key: string
  label: ReactNode
  isNumeric?: boolean
  width?: string
}

export interface ICockpitTileGroupSection {
  name: string
  count: number
  /** Summary shown on the group header, e.g. the group's totals. */
  summary?: string
  summaryCells?: Record<string, ReactNode>
  rows: ReactNode
}

interface ICockpitTileGroupListProps {
  groups: ICockpitTileGroupSection[]
  columns: ICockpitTileGroupColumn[]
  expandedGroups: string[]
  onToggleGroup: (name: string) => void
  emptyLabel?: string
  expandable?: boolean
  groupLabel?: ReactNode
  summaryColumns?: ICockpitTileGroupSummaryColumn[]
}

/**
 * Collapsed-by-default master-detail list: one section per group of the active Group by, opening
 * into the rows behind it. The row markup is the caller's — the two sub-tabs have different
 * columns — while the group frame, the caret and the open/closed handling live here.
 */
export const CockpitTileGroupList = ({
  groups,
  columns,
  expandedGroups,
  onToggleGroup,
  emptyLabel = 'Nothing to show for this grouping.',
  expandable = true,
  groupLabel,
  summaryColumns = [],
}: ICockpitTileGroupListProps) => {
  if (!groups.length) {
    return <div className={styles.empty}>{emptyLabel}</div>
  }

  const hasSummaryColumns = summaryColumns.length > 0
  const summaryGridTemplate = hasSummaryColumns
    ? [
        'minmax(160px, 1fr)',
        ...summaryColumns.map(({ width }) => width ?? 'minmax(72px, 0.4fr)'),
      ].join(' ')
    : undefined

  return (
    <div className={cn(styles.root, { [styles['tabular-root']]: hasSummaryColumns })}>
      {hasSummaryColumns && (
        <div
          className={styles['summary-header']}
          style={{ gridTemplateColumns: summaryGridTemplate }}
        >
          <CockpitTableColumnTitle>{groupLabel}</CockpitTableColumnTitle>
          {summaryColumns.map(({ key, label, isNumeric }) => (
            <div key={key} className={cn({ [styles.numeric]: isNumeric })}>
              <CockpitTableColumnTitle>{label}</CockpitTableColumnTitle>
            </div>
          ))}
        </div>
      )}

      {groups.map(({ name, count, summary, summaryCells, rows }) => {
        const isExpanded = expandable && expandedGroups.includes(name)

        return (
          <div key={name} className={styles.group}>
            <button
              type="button"
              className={cn(styles.head, { [styles['tabular-head']]: hasSummaryColumns })}
              style={{ gridTemplateColumns: summaryGridTemplate }}
              disabled={!expandable}
              onClick={() => onToggleGroup(name)}
              aria-expanded={expandable ? isExpanded : undefined}
            >
              <span className={styles['name-cell']}>
                {expandable && (
                  <span
                    className={cn(styles.caret, { [styles.expanded]: isExpanded })}
                    aria-hidden
                  />
                )}
                <span className={styles.name} title={name}>
                  {name}
                </span>
              </span>
              {hasSummaryColumns ? (
                summaryColumns.map(({ key, isNumeric }) => (
                  <span
                    key={key}
                    className={cn(styles['summary-cell'], { [styles.numeric]: isNumeric })}
                  >
                    {summaryCells?.[key]}
                  </span>
                ))
              ) : (
                <>
                  {summary !== undefined && <span className={styles.summary}>{summary}</span>}
                  <span className={styles.count}>{count}</span>
                </>
              )}
            </button>

            {isExpanded && (
              <div className={styles['table-wrap']}>
                <table className={styles.table}>
                  <colgroup>
                    {columns.map(({ label, width }) => (
                      <col key={label} style={width ? { width } : undefined} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      {columns.map(({ label, isNumeric }) => (
                        <th key={label} className={cn({ [styles.numeric]: isNumeric })}>
                          <CockpitTableColumnTitle>{label}</CockpitTableColumnTitle>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{rows}</tbody>
                </table>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

CockpitTileGroupList.displayName = 'CockpitTileGroupList'
